import { motion } from 'framer-motion';

const features = [
  'Bis max. 7 Seiten',
  'Responsive Design (Mobil & Tablet)',
  'SEO-Optimierung für Freiburg',
  'Kontaktformular',
  'SSL-Zertifikat',
  'DSGVO-Absicherung inkl. Datenschutzerklärung & Cookie-Management',
  '2 kostenlose Änderungen inklusive',
  'Fertig in max. 1 Woche',
];

export default function FreiburgPricing() {
  return (
    <section id="preise" className="py-20 sm:py-28 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <span className="inline-block bg-brand-green/10 text-brand-green text-xs font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wider">
            Transparente Preise
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-caprasimo text-brand-black mb-4">
            Ein Preis. Alles drin.
          </h2>
          <p className="text-lg text-brand-grey max-w-2xl mx-auto">
            Kein Stundensatz, keine Überraschungen – ein fairer Festpreis für Ihr Webdesign in Freiburg.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 items-center">
          {/* Pricing Card */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="relative bg-white rounded-2xl p-8 border-2 border-brand-green shadow-2xl"
          >
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <span className="bg-brand-green text-white px-6 py-2 rounded-full text-sm font-semibold whitespace-nowrap">
                Komplettpaket Freiburg
              </span>
            </div>

            <div className="text-center mt-4 mb-8">
              <div className="flex items-baseline justify-center gap-2">
                <span className="text-6xl font-bold text-brand-black">899€</span>
                <span className="text-brand-grey text-sm">einmalig</span>
              </div>
              <div className="mt-3">
                <span className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-300 text-amber-700 text-xs font-semibold px-3 py-1.5 rounded-full">
                  <i className="ri-time-line text-sm" />
                  Einführungspreis – nur für kurze Zeit
                </span>
              </div>
              <p className="text-xs text-brand-grey mt-2 opacity-70">
                Regulärer Marktpreis: ab 2.000 € – Sie sparen über 55 %
              </p>
            </div>

            <ul className="space-y-3 mb-8">
              {features.map((f, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center bg-brand-green/10 rounded-full flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-xs text-brand-green" />
                  </div>
                  <span className="text-brand-grey text-sm">{f}</span>
                </li>
              ))}
            </ul>

            <a
              href="#kontakt"
              className="block w-full text-center py-4 bg-brand-green hover:bg-brand-green-light text-white rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Jetzt anfragen
            </a>
          </motion.div>

          {/* Comparison */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="space-y-5"
          >
            <h3 className="text-2xl font-caprasimo text-brand-black mb-6">
              Was andere Freiburger Agenturen verlangen
            </h3>
            {[
              { label: 'Große Freiburger Agentur', price: '3.000 – 8.000 €', icon: 'ri-building-2-line', bad: true },
              { label: 'Kleine Freiburger Agentur', price: '1.500 – 3.000 €', icon: 'ri-store-2-line', bad: true },
              { label: 'BR Webdesign (mein Angebot)', price: '899 €', icon: 'ri-award-line', bad: false },
            ].map((item, i) => (
              <div
                key={i}
                className={`flex items-center justify-between rounded-xl px-5 py-4 border-2 transition-all ${
                  item.bad
                    ? 'bg-white border-brand-beige'
                    : 'bg-brand-green/5 border-brand-green shadow-md'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${item.bad ? 'bg-brand-beige-light' : 'bg-brand-green/10'}`}>
                    <i className={`${item.icon} text-lg ${item.bad ? 'text-brand-grey' : 'text-brand-green'}`} />
                  </div>
                  <span className={`text-sm font-medium ${item.bad ? 'text-brand-grey' : 'text-brand-black font-semibold'}`}>
                    {item.label}
                  </span>
                </div>
                <span className={`font-bold text-base whitespace-nowrap ${item.bad ? 'text-red-400 line-through opacity-70' : 'text-brand-green text-xl'}`}>
                  {item.price}
                </span>
              </div>
            ))}

            <div className="bg-brand-beige-light rounded-xl p-5 mt-4">
              <p className="text-sm text-brand-grey leading-relaxed">
                <strong className="text-brand-black">Sie sparen bis zu 7.100 €</strong> gegenüber einer großen Freiburger Agentur – bei gleicher oder besserer Qualität und deutlich kürzerer Lieferzeit.
              </p>
            </div>

            <a
              href="/pricing"
              className="inline-flex items-center gap-2 text-brand-green hover:text-brand-green-light font-semibold text-sm transition-colors cursor-pointer"
            >
              Vollständige Preisübersicht ansehen
              <i className="ri-arrow-right-line" />
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
